import React, { createContext, useContext, useState } from 'react';

export type CreateRatingStep = 'CREATE' | 'TERMS' | 'PREVIEW' | 'SUBMIT_SUCCESS' | 'SUBMIT_ERROR';

interface CreateRatingContextProps {
  setRatingValue: (value: number) => void;
  getRatingValue: () => number;
  setHoveredRating: (value: number) => void;
  getHoveredRating: () => number;
  resetRatingValues: (resetStep?: boolean) => void;
  setRatingHeadline: (headline: string) => void;
  getRatingHeadline: () => string;
  setRatingReview: (review: string) => void;
  getRatingReview: () => string;
  setReviewerName: (name: string) => void;
  getReviewerName: () => string;
  setReviewerCity: (city: string) => void;
  getReviewerCity: () => string;
  pushAdvantageTag: (tag: string) => void;
  removeAdvantageTag: (tag: string) => void;
  getAdvantageTags: () => string[];
  pushDisadvantageTag: (tag: string) => void;
  removeDisadvantageTag: (tag: string) => void;
  getDisadvantageTags: () => string[];
  setTermsAccepted: (accepted: boolean) => void;
  getTermsAccepted: () => boolean;
  setCreationStep: (step: CreateRatingStep) => void;
  getCreationStep: () => CreateRatingStep;
}

export const CreateRatingContext = createContext<CreateRatingContextProps>(null);

export const useCreateRatingContext = (): CreateRatingContextProps =>
  useContext(CreateRatingContext);

export const CreateRatingContextProvider = ({ children, ...props }): JSX.Element => {
  const [value, setValue] = useState<number>(null);
  const [hoveredValue, setHoveredValue] = useState<number>(0);
  const [headline, setHeadline] = useState<string>('');
  const [review, setReview] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [advantages, setAdvantages] = useState<string[]>([]);
  const [disadvantages, setDisadvantages] = useState<string[]>([]);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(null);
  const [step, setStep] = useState<CreateRatingStep>('CREATE');

  return (
    <CreateRatingContext.Provider
      value={{
        getAdvantageTags: () => advantages,
        getCreationStep: () => step,
        getDisadvantageTags: () => disadvantages,
        getHoveredRating: () => hoveredValue,
        getRatingHeadline: () => headline,
        getRatingReview: () => review,
        getRatingValue: () => value,
        getReviewerCity: () => city,
        getReviewerName: () => name,
        getTermsAccepted: () => isTermsAccepted,
        pushAdvantageTag: (advantage) => {
          let newAdvantages: string[] = [];
          newAdvantages = newAdvantages.concat(advantages);
          newAdvantages.push(advantage);
          setAdvantages(newAdvantages);
        },
        pushDisadvantageTag: (disadvantage) => {
          let newDisadvantages: string[] = [];
          newDisadvantages = newDisadvantages.concat(disadvantages);
          newDisadvantages.push(disadvantage);
          setDisadvantages(newDisadvantages);
        },
        removeAdvantageTag: (advantage) => {
          let newAdvantages: string[] = [];
          newAdvantages = newAdvantages.concat(advantages);
          newAdvantages = newAdvantages.filter((adv) => adv !== advantage);
          setAdvantages(newAdvantages);
        },
        removeDisadvantageTag: (disadvantage) => {
          let newDisadvantages: string[] = [];
          newDisadvantages = newDisadvantages.concat(advantages);
          newDisadvantages = newDisadvantages.filter((disadv) => disadv !== disadvantage);
          setDisadvantages(newDisadvantages);
        },
        resetRatingValues: (resetStep = true) => {
          setValue(null);
          setHoveredValue(0);
          setHeadline('');
          setReview('');
          setName('');
          setCity('');
          setAdvantages([]);
          setDisadvantages([]);
          setIsTermsAccepted(null);
          if (resetStep) {
            setStep('CREATE');
          }
        },
        setCreationStep: (creationStep) => setStep(creationStep),
        setHoveredRating: (hoveredRating) => setHoveredValue(hoveredRating),
        setRatingHeadline: (ratingHeadline) => setHeadline(ratingHeadline),
        setRatingReview: (ratingReview) => setReview(ratingReview),
        setRatingValue: (ratingValue) => setValue(ratingValue),
        setReviewerCity: (reviewerCity) => setCity(reviewerCity),
        setReviewerName: (reviewerName) => setName(reviewerName),
        setTermsAccepted: (accepted) => setIsTermsAccepted(accepted),
      }}
      // props need to be passed in this way
      // eslint-disable-next-line
      {...props}
    >
      {children}
    </CreateRatingContext.Provider>
  );
};

export default CreateRatingContextProvider;
